import React, { useState, useEffect } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Alert from '@mui/material/Alert';
//import StoryBookPage from './StoryBookPage';



const TextStoryPage = ({
  storyTitle,
  storyPages,
  storyLanguage,
  voice,
  imageStyle,
  onGenerateMedia,
  mediaUrl,
  media,
  mediaLoading,
  mediaError = '',
  mediaErrorSeverity = 'error',
  t,
  onClose,
  userPlan, // pass userPlan from parent (same as PurchasePage)
  contentType = 'social_story',
  disableGenerateMedia = false,
  showMediaLoadingDialog = true,
}) => {

  // Compute userPlanWeight from userPlan prop
  const userPlanWeight = (userPlan && typeof userPlan === 'object' && 'weight' in userPlan)
    ? userPlan.weight
    : 0;

  // Dialog state for upgrade prompt
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  // State for editable story text and title
  const [editedPages, setEditedPages] = useState(() =>
    storyPages ? storyPages.map(page => [...page.sentences]) : []
  );
  const [editedTitle, setEditedTitle] = useState(storyTitle || "");
  const [editMode, setEditMode] = useState(false);
  // Store the last saved (or edited) story and title for display and media
  const [displayPages, setDisplayPages] = useState(() =>
    storyPages ? storyPages.map(page => [...page.sentences]) : []
  );
  const [displayTitle, setDisplayTitle] = useState(storyTitle || "");

  // Keep editedPages and title in sync if storyPages or storyTitle changes
  useEffect(() => {
    setEditedPages(storyPages ? storyPages.map(page => [...page.sentences]) : []);
    setDisplayPages(storyPages ? storyPages.map(page => [...page.sentences]) : []);
  }, [storyPages]);
  useEffect(() => {
    setEditedTitle(storyTitle || "");
    setDisplayTitle(storyTitle || "");
  }, [storyTitle]);

  if (!storyTitle || !storyPages || storyPages.length === 0) return null;

  // Handler for editing a sentence
  const handleEditSentence = (pageIdx, sentIdx, value) => {
    setEditedPages(prev => {
      const newPages = prev.map(arr => [...arr]);
      newPages[pageIdx][sentIdx] = value;
      return newPages;
    });
  };

  // Handler for Generate Media using last saved edits (not editMode)
  const handleGenerateMedia = () => {
    if (onGenerateMedia) {
      onGenerateMedia(
        voice,
        imageStyle,
        storyLanguage,
        displayPages.map((sentences, idx) => ({ ...storyPages[idx], sentences })),
        displayTitle
      );
    }
  };

  const handleOpenInStoryPlayer = () => {
    if (!media || media.length === 0) return;
    const payload = {
      storyTitle: displayTitle,
      media,
    };
    try {
      sessionStorage.setItem('story_player_preview', JSON.stringify(payload));
      window.open('/story-player', '_blank');
    } catch (err) {
      console.error('Failed to open in Story Player:', err);
    }
  };

  // Handler for toggling edit mode and saving edits
  const handleEditToggle = () => {
    // Only allow editing if plan weight >= 45
    // if (!editMode && contentType !== 'visual_schedule' && userPlanWeight < 45) {
    //   setUpgradeDialogOpen(true);
    //   return;
    // }
    if (editMode) {
      // Save edits: update display values
      setDisplayPages(editedPages.map(arr => [...arr]));
      setDisplayTitle(editedTitle);
    }
    setEditMode(em => !em);
  };

  return (
    <Box sx={{ position: 'relative', pt: 2 }}>
      {/* Close icon for popup dialog at upper right, above the title, with higher zIndex */}
      {onClose && (
        <Box sx={{ position: 'absolute', right: 12, top: -12, zIndex: 2000 }}>
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="large"
            sx={{ background: 'rgba(255,255,255,0.85)', boxShadow: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <Box sx={{ mb: 2, pr: 7, textAlign: 'center' }}>
        {editMode ? (
          <input
            type="text"
            value={editedTitle}
            onChange={e => setEditedTitle(e.target.value)}
            style={{
              width: '100%',
              fontWeight: 600,
              fontSize: '1.5rem',
              textAlign: 'center',
              border: '1px solid #ccc',
              borderRadius: 6,
              padding: 8,
              marginBottom: 4
            }}
            disabled={mediaLoading}
          />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.7rem' }, mb: 1 }}>{displayTitle}</Typography>
        )}
      </Box>

      {storyPages.map((page, idx) => (
        <Box key={idx} sx={{ mb: 2, border: '1px solid #eee', borderRadius: 2, p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{`${idx + 1}/${storyPages.length}`}</Typography>
          <ul style={{ paddingLeft: 0 }}>
            {(editMode ? editedPages[idx] : displayPages[idx]).map((s, i) => (
              <li key={i} style={{ listStyle: 'none', marginBottom: 8 }}>
                {editMode ? (
                  <textarea
                    value={editedPages[idx]?.[i] || ''}
                    onChange={e => handleEditSentence(idx, i, e.target.value)}
                    rows={2}
                    style={{ width: '100%', resize: 'vertical', fontSize: '1rem', padding: 6, borderRadius: 4, border: '1px solid #ccc' }}
                    disabled={mediaLoading}
                  />
                ) : (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line', fontSize: '1rem', color: '#333', px: 1 }}>{s}</Typography>
                )}
              </li>
            ))}
          </ul>
        </Box>
      ))}


      {/* Edit/Save and Generate Media buttons */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, my: 2, alignItems: 'center' }}>
        <Button
          variant={editMode ? 'outlined' : 'contained'}
          color={editMode ? 'inherit' : 'inherit'}
          onClick={handleEditToggle}
          disabled={mediaLoading}
          sx={editMode
            ? { minWidth: 120, borderColor: '#274D3B', color: '#274D3B', '&:hover': { borderColor: '#1F3D30', backgroundColor: '#EDF1DF', color: '#1F3D30' } }
            : { minWidth: 120 }
          }
        >
          {/* Multi-language support for Edit/Save button label */}
          {editMode
            ? (contentType === 'visual_schedule'
              ? (t?.sb_save_schedule_steps || 'Save Steps')
              : (t?.sb_save_edits || (t?.save || 'Save Edits')))
            : (contentType === 'visual_schedule'
              ? (t?.sb_edit_schedule_steps || 'Edit Steps')
              : (t?.sb_edit_story || (t?.edit_story || 'Edit Story')))}
        </Button>

        {/* Upgrade dialog if plan weight < 45 */}
        <Dialog open={upgradeDialogOpen} onClose={() => setUpgradeDialogOpen(false)} maxWidth="xs" fullWidth sx={{ zIndex: 3000 }}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Typography variant="h6" align="center" sx={{ mb: 2 }}>
              {t?.sb_upgrade_required || 'To access this feature, please upgrade your plan.'}
            </Typography>
            <Button variant="contained" color="black" onClick={() => setUpgradeDialogOpen(false)}>
              {t?.close || 'Close'}
            </Button>
          </DialogContent>
        </Dialog>
        <Button
          variant="contained"
          color="inherit"
          onClick={handleGenerateMedia}
          disabled={
            mediaLoading ||
            !storyPages.length ||
            disableGenerateMedia ||
            (!!mediaUrl && !mediaLoading && !/\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl))
          }
          sx={{ backgroundColor: '#274D3B', color: 'white', '&:hover': { backgroundColor: '#1F3D30' } }}
        >
          {mediaLoading ? (t?.media_generating || 'Generating Media...') : (t?.media_generation_button || 'Generate Media')}
        </Button>

        {/* Display Story button for non-image mediaUrl */}
        {media && media.length > 0 && !mediaLoading && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleOpenInStoryPlayer}
            sx={{ ml: { sm: 2, xs: 0 }, minWidth: 160, backgroundColor: '#111', color: '#fff', '&:hover': { backgroundColor: '#222' } }}
          >
            {t?.sb_display_story || 'Display Story'}
          </Button>
        )}
        {/* For legacy support of mediaUrl (if media prop is not used), show Display Story button if mediaUrl is a non-image link */}
        {/* {mediaUrl && !mediaLoading && !/\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl) && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => window.open(mediaUrl, '_blank')}
            sx={{ ml: { sm: 2, xs: 0 }, borderColor: 'black', color: 'black', '&:hover': { backgroundColor: '#222', color: 'white', borderColor: 'black' } }}
          >
            {t?.sb_display_story || 'Display Story'}
          </Button>
        )} */}

      {/* MediaStoryPage displays generated media (image or link)
      <MediaStoryPage mediaUrl={mediaUrl} isLoading={mediaLoading} /> */}
      </Box>

      {mediaError ? (
        <Alert severity={mediaErrorSeverity} sx={{ mt: 2, mb: 1, width: '100%', boxSizing: 'border-box' }}>
          {mediaError}
        </Alert>
      ) : null}

      {/* Progress bar popup while generating media */}
      <Dialog open={showMediaLoadingDialog && mediaLoading} maxWidth="xs" fullWidth sx={{ zIndex: 2500 }}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <LinearProgress sx={{ width: '100%', mb: 2 }} />
          <Typography variant="h6" align="center">
            {t?.media_generation_lbl || 'Generating Media...'}
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Remove duplicate close icon below */}
    </Box>
  );
};

export default TextStoryPage;
